import React, { useCallback, useEffect, useState } from "react";
import { cmApi } from "./cmApi";
import BundleItemPicker from "../../../components/compliance/mvp/BundleItemPicker";

export default function BundleBuilder() {
  const [bundles, setBundles] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const [b, p] = await Promise.all([cmApi.listBundles(), cmApi.listPolicies()]);
    setBundles(b);
    setPolicies(p);
    if (selected) {
      const fresh = await cmApi.getBundle(selected._id);
      setSelected(fresh);
    }
  }, [selected]);

  useEffect(() => {
    cmApi.listBundles().then(setBundles).catch(() => {});
    cmApi.listPolicies().then(setPolicies).catch(() => {});
  }, []);

  const createBundle = async () => {
    if (!name) return;
    const b = await cmApi.createBundle(name, "Created from MVP UI", []);
    setName("");
    setSelected(b);
    load();
  };

  const addItem = async (item) => {
    const updated = await cmApi.addBundleItem(selected._id, item);
    setSelected(updated);
    load();
  };

  const exportBundle = async () => {
    setStatus("Exporting…");
    try {
      const res = await cmApi.exportBundle(selected._id);
      setStatus(`Exported ${res.page_count} page(s) → ${res.file_path}`);
      load();
    } catch (e) {
      setStatus("Export failed (PDF libraries unavailable on server).");
    }
  };

  return (
    <div className="p-6" data-testid="cm-bundle-builder">
      <h1 className="text-xl font-semibold text-slate-800">Bundle Builder</h1>

      <div className="mt-4 flex items-center gap-2">
        <input
          placeholder="New bundle name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
          data-testid="cm-bundle-name"
        />
        <button
          onClick={createBundle}
          className="rounded bg-indigo-600 px-3 py-1 text-sm text-white"
          data-testid="cm-create-bundle"
        >
          Create
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Bundles</h2>
          <ul className="mt-2 space-y-2">
            {bundles.map((b) => (
              <li key={b._id}>
                <button
                  onClick={() => cmApi.getBundle(b._id).then(setSelected)}
                  className={`w-full rounded border px-3 py-2 text-left text-sm ${
                    selected && selected._id === b._id
                      ? "border-indigo-400 bg-indigo-50"
                      : "border-slate-200"
                  }`}
                  data-testid="cm-bundle-row"
                >
                  <span className="text-slate-800">{b.name}</span>
                  <span className="ml-2 text-xs text-slate-400">{b.status}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {selected && (
          <div data-testid="cm-bundle-detail">
            <h2 className="text-sm font-semibold text-slate-700">{selected.name}</h2>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {(selected.items || []).map((it, i) => (
                <li key={i} className="rounded bg-slate-50 px-2 py-1">
                  {it.title || it.ref_id}
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <BundleItemPicker policies={policies} onAdd={addItem} />
            </div>
            <button
              onClick={exportBundle}
              className="mt-4 rounded bg-emerald-600 px-3 py-1 text-sm text-white"
              data-testid="cm-export-bundle"
            >
              Export PDF
            </button>
            {status && <p className="mt-2 text-sm text-slate-500">{status}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
